/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect } from 'react';
import { Button, Input, message, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  editShare,
  resendEditInviteShare,
  updateShare,
} from '~src/services/api/shares';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DB_STATE } from '~src/types/enums';
import { IconButtonProps } from '~components/_common/IconButton/IconButton';
import {
  getShareStatusVisual,
  getShareStatusVisualIntl,
} from '~src/utils/shareUtils/useShareKeysStatusUtils';

export type EditShareTenderModalProps = Pick<ModalProps, 'visible'> & {
  projectId: number;
  tenderId: number;
  shareKey: string;
  previousData?: any;
  onCancel?: () => void;
};

export const DetailsEditInviteModal: React.FC<EditShareTenderModalProps> = ({
  projectId,
  tenderId,
  previousData,
  onCancel,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const sharesForm = useForm({
    defaultValues: {
      name: '',
      http: '',
      phone: '',
      email: previousData.share_key_email || '',
      note: previousData.share_description || '',
    },
  });

  const [updateShareMutation, { isLoading }] = useMutation(
    resendEditInviteShare,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'share-edit-failed' }));
        appLog('updateShareMutation -> item', item);
        appLog('updateShareMutation -> err', error);
      },
    }
  );

  const [
    updateTenderShareStateMutation,
    { isLoading: isUpdateShareStateLoading },
  ] = useMutation(updateShare, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'tender-share-update-failed' }));
      appLog('updateTenderShareStateMutation -> item', item);
      appLog('updateTenderShareStateMutation -> err', error);
    },
  });

  const isValid = () => {
    const { email } = sharesForm.getValues();
    if (!email) {
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!isValid()) return;

      const { note } = sharesForm.getValues();
      await updateShareMutation({
        projectId,
        tenderId,
        shareKey: previousData?.share_key_id,
        description: note,
      });
      onCancel?.();
    } catch (err) {
      message.error(intl.formatMessage({ id: 'share-edit-failed' }));
    }
  };

  const handleReactivateShare = async () => {
    try {
      if (!isValid()) return;
      if (projectId && tenderId && previousData) {
        await updateTenderShareStateMutation({
          projectId,
          tenderId: Number(tenderId),
          shareKey: previousData.share_key_id,
          action: 'reactivate',
        });
      }
    } catch (err) {
      appLog('handleReactivateShare -> err', err);
    }
  };

  useEffect(() => {
    const values = sharesForm.getValues();
    if (previousData && !values.email) {
      sharesForm.reset({
        email: previousData.share_key_email,
        note: previousData.share_description,
      });
    }
  }, [previousData]);

  return (
    <div css={styles.formContainer}>
      {previousData &&
        (() => {
          const { icon, color, text } = getShareStatusVisualIntl(
            intl,
            previousData
          );
          return (
            <div css={styles.formUpper}>
              <div css={styles.formStatus}>
                <div>{intl.formatMessage({ id: 'status' })}</div>
                <div style={{ color }}>
                  {icon} {text}
                </div>
              </div>
              <div css={styles.formAcceptedAs}>
                <div>Invitation accepted as</div>
                <div css={styles.formAcceptedDetails}>
                  <span>{previousData?.share_key_title || 'No name'}</span>
                  <span>{previousData?.phone || 'No job'}</span>
                  <span>{previousData?.http || 'No http'}</span>
                  <span>{previousData?.phone || 'No phone'}</span>
                </div>
              </div>
            </div>
          );
        })()}
      <div css={styles.formItem}>
        <p css={styles.formLabel}>Invitation e-mail sent to</p>
        <Controller
          name="email"
          control={sharesForm.control}
          render={({ field }) => (
            <Input
              disabled={previousData.share_key_claimed_at > 0}
              {...field}
            />
          )}
        />
      </div>
      <div css={styles.formItem}>
        <p css={styles.formLabel}>
          {intl.formatMessage({ id: 'description' })}
        </p>
        <Controller
          name="note"
          control={sharesForm.control}
          render={({ field }) => <Input {...field} />}
        />
      </div>
      <div css={styles.formItem}>
        <Button
          type="primary"
          loading={isLoading || isUpdateShareStateLoading}
          onClick={async () => {
            if (previousData.share_key_state === 9) {
              await handleReactivateShare();
            }
            await handleSubmit();
          }}>
          {previousData.share_key_state === 9
            ? intl.formatMessage({ id: 'reactivate-and-resend' })
            : intl.formatMessage({ id: 'resend-invite' })}
        </Button>
      </div>
    </div>
  );
};

export const styleFn: ThemedStyleFn = (theme) => ({
  // Form
  formContainer: {
    margin: 10,
  },
  formUpper: {
    // backgroundColor: 'red',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  formStatus: {
    // backgroundColor: 'blue',
    display: 'flex',
    gap: '120px',
  },
  formAcceptedAs: {
    display: 'flex',
    gap: '30px',
    height: '100px',
  },
  formAcceptedDetails: {
    display: 'flex',
    flexDirection: 'column',
    color: 'gray',
  },
  buttonsBlock: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  leftButton: {
    marginRight: 10,
  },
  formItem: {
    marginBottom: 12,
  },
  formLabel: {
    marginBottom: 12,
  },
  cardContainer: {
    marginTop: 10,
    borderRadius: 10,
  },
  dateText: {
    textAlign: 'start',
    fontSize: 12,
    paddingRight: 8,
  },
  mergeContainer: {
    display: 'flex',
  },
  twoColumnsField: {
    width: '50%',
    margin: '4px 0px 15px -1px',
  },
});
